// CO-160a multiview — layout constants and pure helpers.
//
// Extracted into a `.ts` module (no JSX) so the geometry, tally
// resolution, and label helpers are unit-testable under
// `node --test --experimental-strip-types`. The React component imports
// from here; tests do too. Single source of truth for tile positions.
//
// Fidelity contract: docs/insights/tier-2-vector-fidelity.md

/** Canonical CO-160a small-tile source ids. */
export type MultiviewSourceId =
  | "cam1"
  | "cam2"
  | "cam3"
  | "media1"
  | "media2";

/** Anything else that may sit on PGM/PVW per scenario engine state. */
export type MultiviewBusSource = MultiviewSourceId | "black" | (string & {});

export interface MultiviewSources {
  cam1?: string;
  cam2?: string;
  cam3?: string;
  media1?: string;
  media2?: string;
}

export const DEFAULT_MULTIVIEW_LABELS: Required<MultiviewSources> = {
  cam1: "CAM 1",
  cam2: "CAM 2",
  cam3: "CAM 3",
  media1: "MEDIA 1",
  media2: "MEDIA 2",
};

/**
 * Tile geometry per the reference SVG's `viewBox="0 0 640 480"`. Tally
 * borders are inset by 2px on each side of the tile body.
 */
export interface TileBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Geometry derivation (bezel inner: x=36..604, y=36..384, w=568 × h=348):
//   - Symmetric 12-px outer inset on left and right → usable x-range 48..592 (span 544).
//   - Big tiles: width 267, gap 10. preview 48..315, program 325..592.
//   - Small-tile columns: width 130, gap 8.
//     col0 48..178, col1 186..316, col2 324..454, col3 462..592.
export const TILE_BOXES = {
  /** Big PREVIEW tile (top-left). */
  previewBig: { x: 48, y: 48, width: 267, height: 168 } as TileBox,
  /** Big PROGRAM tile (top-right). */
  programBig: { x: 325, y: 48, width: 267, height: 168 } as TileBox,
  /** Camera row — UNUSED · CAM 1 · CAM 2 · CAM 3. */
  cam1: { x: 186, y: 226, width: 130, height: 76 } as TileBox,
  cam2: { x: 324, y: 226, width: 130, height: 76 } as TileBox,
  cam3: { x: 462, y: 226, width: 130, height: 76 } as TileBox,
  /** Media row — MEDIA 1 · MEDIA 2 · UNUSED · UNUSED. */
  media1: { x: 48, y: 312, width: 130, height: 68 } as TileBox,
  media2: { x: 186, y: 312, width: 130, height: 68 } as TileBox,
} as const;

/** Tally inset relative to the tile box. */
export const TALLY_INSET = 2;

/** Resolve a tally rectangle for a given small-tile source. */
export function tallyBoxFor(id: MultiviewSourceId): TileBox {
  const box = TILE_BOXES[id];
  return {
    x: box.x + TALLY_INSET,
    y: box.y + TALLY_INSET,
    width: box.width - 2 * TALLY_INSET,
    height: box.height - 2 * TALLY_INSET,
  };
}

/**
 * Resolve which small tile corresponds to a given bus source. Returns
 * `null` for `black`, UNUSED, or unknown sources — those cases get no
 * tally on the small grid (only the big tile lights up).
 */
export function tileForSource(src: MultiviewBusSource): MultiviewSourceId | null {
  switch (src) {
    case "cam1":
      return "cam1";
    case "cam2":
      return "cam2";
    case "cam3":
      return "cam3";
    case "media1":
      return "media1";
    case "media2":
      return "media2";
    default:
      return null;
  }
}

/** Friendly display label for a bus source on the big tile. */
export function multiviewLabelFor(
  src: MultiviewBusSource,
  sources?: MultiviewSources,
): string {
  if (src === "black") return "BLACK";
  const labels = { ...DEFAULT_MULTIVIEW_LABELS, ...sources };
  const tile = tileForSource(src);
  return tile ? labels[tile] : String(src).toUpperCase();
}
